import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import './App.css';

import { PublicLayout } from './components/PublicLayout';
import Entrance from './pages/Entrance';
import Home from './pages/Home';
import StartHere from './pages/StartHere';
import PaperBallots from './pages/PaperBallots';
import WitnessYourVote from './pages/WitnessYourVote';
import Resources from './pages/Resources';
import GetInvolved from './pages/GetInvolved';
import Reviews from './pages/Reviews';
import AdjustPin from './pages/AdjustPin';
import LeaveReview from './pages/LeaveReview';
import SunshineList from './pages/SunshineList';
import LowerTaxes from './pages/LowerTaxes';
import CaoReview from './pages/CaoReview';
import CityFinances from './pages/CityFinances';
import ForensicAudit from './pages/ForensicAudit';
import MediaForThePeople from './pages/MediaForThePeople';
import Platform from './pages/Platform';
import Ward8 from './pages/Ward8';
import ActionHub from './pages/ActionHub';
import ReportIssue from './pages/ReportIssue';
import TrackMyIssue from './pages/TrackMyIssue';
import WhatsHappening from './pages/WhatsHappening';
import Meetings, { MeetingDetail } from './pages/Meetings';
import CityData from './pages/CityData';
import TaxTracker from './pages/TaxTracker';
import InfrastructureTracker from './pages/InfrastructureTracker';
import HowToHaveYourSay from './pages/HowToHaveYourSay';
import CityHallWatch from './pages/CityHallWatch';
import PublicMap from './pages/PublicMap';
import PublicIssue from './pages/PublicIssue';
import SavingsTracker from './pages/SavingsTracker';
import WhoDoICall from './pages/WhoDoICall';
import Reports from './pages/Reports';
import Newsroom, { NewsArticle } from './pages/Newsroom';
import Gallery from './pages/Gallery';
import Videos from './pages/Videos';
import Volunteer from './pages/Volunteer';
import Contact from './pages/Contact';
import Alerts from './pages/Alerts';
import Elections from './pages/Elections';
import WhosRunning from './pages/WhosRunning';
import TrackerNetwork from './pages/TrackerNetwork';
import JoinNetwork from './pages/JoinNetwork';
import PitaRequestAccess from './pages/PitaRequestAccess';
import SupportPita from './pages/SupportPita';
import PitaSupporters from './pages/PitaSupporters';
import CitywideView from './pages/CitywideView';
import HostedTracker from './pages/HostedTracker';
import EmbedTracker from './pages/EmbedTracker';
import { HubErrorBoundary } from './components/HubErrorBoundary';

import AdminLogin from './pages/admin/AdminLogin';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminPages from './pages/admin/AdminPages';
import AdminHero from './pages/admin/AdminHero';
import AdminPosters from './pages/admin/AdminPosters';
import AdminMenu from './pages/admin/AdminMenu';
import AdminPageContent from './pages/admin/AdminPageContent';
import AdminNeedsAttention from './pages/admin/AdminNeedsAttention';
import AdminIssues from './pages/admin/AdminIssues';
import AdminNewsletter from './pages/admin/AdminNewsletter';
import AdminChat from './pages/admin/AdminChat';
import AdminSocial from './pages/admin/AdminSocial';
import AdminSettings from './pages/admin/AdminSettings';
import AdminLaunch from './pages/admin/AdminLaunch';
import AdminLaunchContent from './pages/admin/AdminLaunchContent';
import AdminMediaReview from './pages/admin/AdminMediaReview';
import AdminPrivacy from './pages/admin/AdminPrivacy';
import AdminDirectory from './pages/admin/AdminDirectory';
import AdminTrackers from './pages/admin/AdminTrackers';
import AdminHaveYourSay from './pages/admin/AdminHaveYourSay';
import AdminAutomation from './pages/admin/AdminAutomation';
import AdminElection from './pages/admin/AdminElection';
import AdminReportsPage from './pages/admin/AdminReportsPage';
import AdminCampaign from './pages/admin/AdminCampaign';
import AdminSavings from './pages/admin/AdminSavings';
import AdminScheduler from './pages/admin/AdminScheduler';
import AdminNetwork from './pages/admin/AdminNetwork';
import AdminStoryMedia from './pages/admin/AdminStoryMedia';
import AdminDesigns from './pages/admin/AdminDesigns';
import AdminRehearsal from './pages/admin/AdminRehearsal';
import AdminPosterDrops from './pages/admin/AdminPosterDrops';
import AdminMaterials from './pages/admin/AdminMaterials';
import AdminDoorTalk from './pages/admin/AdminDoorTalk';
import AdminCanvassLog from './pages/admin/AdminCanvassLog';
import AdminSupporters from './pages/admin/AdminSupporters';
import AdminLaunchPackage from './pages/admin/AdminLaunchPackage';
import AdminDomainActivation from './pages/admin/AdminDomainActivation';
import AdminLaunchRunbook from './pages/admin/AdminLaunchRunbook';
import AdminPostLaunchPulse from './pages/admin/AdminPostLaunchPulse';
import AdminFirstResponses from './pages/admin/AdminFirstResponses';
import AdminContactInbox from './pages/admin/AdminContactInbox';
import AdminPitaRequests from './pages/admin/AdminPitaRequests';
import AdminPitaSupporters from './pages/admin/AdminPitaSupporters';
import AdminPitaSettings from './pages/admin/AdminPitaSettings';
import AdminReviews from './pages/admin/AdminReviews';
import AdminEmailSettings from './pages/admin/AdminEmailSettings';
import AdminElectionEvents from './pages/admin/AdminElectionEvents';
import AdminVideoLibrary from './pages/admin/AdminVideoLibrary';
import Supporters from './pages/Supporters';
// Site structure v3 (Phase 123)
import RequestSign from './pages/RequestSign';
import Donate from './pages/Donate';
import About from './pages/About';
import CvFlyer from './pages/about/CvFlyer';
import ElectionTools from './pages/about/ElectionTools';
import MediaHub from './pages/MediaHub';
import AdminSignRequests from './pages/admin/AdminSignRequests';
import AdminWard8Photos from './pages/admin/AdminWard8Photos';
import AdminImageHealth from './pages/admin/AdminImageHealth';
import {
  AdminNewsroom, AdminMeetings, AdminCityData, AdminAlerts, AdminGallery,
  AdminStories, AdminCalendar, AdminVolunteers,
} from './pages/admin/crudPages';

function App() {
  // Legacy redirect: /action-hub* -> /issue-tracker* (preserves ?track= etc.)
  const LegacyHubRedirect = ({ to }) => {
    const location = useLocation();
    return <Navigate to={{ pathname: to, search: location.search }} replace />;
  };
  // Generic content-preserving redirect (keeps ?query and #hash unless given).
  const R = ({ to, hash = '', search = '' }) => {
    const location = useLocation();
    return <Navigate to={{ pathname: to, search: search || location.search, hash: hash || location.hash }} replace />;
  };
  return (
    <BrowserRouter>
      <Routes>
        {/* Campaign entrance: minimal full-screen gateway (Phase 92). Rendered
            WITHOUT the site layout. The full homepage lives at /home. */}
        <Route path="/" element={<Entrance />} />
        <Route element={<PublicLayout />}>
          <Route path="/home" element={<Home />} />
          {/* ---- Site structure v3 (Phase 123): new top-level pages ---- */}
          <Route path="/request-a-sign" element={<RequestSign />} />
          <Route path="/donate" element={<Donate />} />
          <Route path="/about" element={<About />} />
          <Route path="/about/cv-flyer" element={<CvFlyer />} />
          <Route path="/about/election-tools" element={<ElectionTools />} />
          <Route path="/media" element={<MediaHub />} />
          {/* v3 aliases: every document heading resolves to the page that holds
              that content (never just a landing page). Old URLs stay live. */}
          <Route path="/request-sign" element={<R to="/request-a-sign" />} />
          <Route path="/lawn-sign" element={<R to="/request-a-sign" />} />
          <Route path="/signs" element={<R to="/request-a-sign" />} />
          <Route path="/donations" element={<R to="/donate" />} />
          <Route path="/contribute" element={<R to="/donate" />} />
          <Route path="/downloads" element={<R to="/about/cv-flyer" />} />
          <Route path="/cv" element={<R to="/about/cv-flyer" hash="#cv" />} />
          <Route path="/flyer" element={<R to="/about/cv-flyer" hash="#flyer" />} />
          <Route path="/about/platform" element={<R to="/platform" />} />
          <Route path="/about/reviews" element={<R to="/reviews" />} />
          <Route path="/about/why-im-running" element={<R to="/start-here" hash="#why-im-running" />} />
          <Route path="/about/steve" element={<R to="/start-here" />} />
          <Route path="/election-tools" element={<R to="/about/election-tools" />} />
          <Route path="/media/videos" element={<R to="/videos" />} />
          <Route path="/media/two-minutes" element={<R to="/videos" search="?category=two_minutes" />} />
          <Route path="/media/events" element={<R to="/videos" search="?category=election_events" />} />
          <Route path="/media/other" element={<R to="/videos" search="?category=other" />} />
          <Route path="/media/posters" element={<R to="/paper-ballots" hash="#series" />} />
          <Route path="/media/paper-ballot-series" element={<R to="/paper-ballots" hash="#series" />} />
          <Route path="/media/photos" element={<R to="/gallery" search="?album=ward8" />} />
          <Route path="/media/beautiful-ward-8" element={<R to="/gallery" search="?album=ward8" />} />
          <Route path="/photos" element={<R to="/gallery" />} />
          <Route path="/election-event-list" element={<R to="/elections/events" />} />
          <Route path="/events" element={<R to="/elections/events" />} />
          <Route path="/pita" element={<R to="/issue-tracker" />} />
          <Route path="/ward-boundaries" element={<R to="/elections/ward-boundaries" />} />
          <Route path="/how-to-vote" element={<R to="/elections/how-to-vote" />} />
          <Route path="/start-here" element={<StartHere />} />
          {/* Restructure v0.2 redirects — old biography URLs keep working forever */}
          <Route path="/meet-steve" element={<Navigate to="/start-here" replace />} />
          <Route path="/meet-steve/story" element={<Navigate to={{ pathname: '/start-here', hash: '#story' }} replace />} />
          <Route path="/meet-steve/experience" element={<Navigate to={{ pathname: '/start-here', hash: '#experience' }} replace />} />
          <Route path="/meet-steve/bio" element={<Navigate to={{ pathname: '/start-here', hash: '#bio' }} replace />} />
          <Route path="/why-im-running" element={<Navigate to={{ pathname: '/start-here', hash: '#why-im-running' }} replace />} />
          <Route path="/experience" element={<Navigate to={{ pathname: '/start-here', hash: '#experience' }} replace />} />
          <Route path="/platform" element={<Platform />} />
          <Route path="/paper-ballots" element={<PaperBallots />} />
          <Route path="/paper-ballots/:part" element={<PaperBallots />} />
          <Route path="/witness-your-vote" element={<WitnessYourVote />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/get-involved" element={<GetInvolved />} />
          <Route path="/reviews" element={<Reviews />} />
          <Route path="/leave-a-review" element={<LeaveReview />} />
          <Route path="/sunshine-list" element={<SunshineList />} />
          <Route path="/lower-taxes" element={<LowerTaxes />} />
          <Route path="/cao-review" element={<CaoReview />} />
          <Route path="/city-finances" element={<CityFinances />} />
          <Route path="/forensic-audit" element={<ForensicAudit />} />
          <Route path="/media-for-the-people" element={<MediaForThePeople />} />
          <Route path="/ward-8" element={<Ward8 />} />
          <Route path="/issue-tracker" element={<HubErrorBoundary section="Ward 8 Public Issue Tracker App (PITA)" apiHint="/api/issues, /api/issue-categories"><ActionHub /></HubErrorBoundary>} />
          <Route path="/issue-tracker/report" element={<HubErrorBoundary section="Report Issue Wizard" apiHint="/api/issues, /api/issue-categories"><ReportIssue /></HubErrorBoundary>} />
          <Route path="/issue-tracker/pin" element={<HubErrorBoundary section="Adjust My Pin" apiHint="/api/issues/pin"><AdjustPin /></HubErrorBoundary>} />
          <Route path="/issue-tracker/track" element={<HubErrorBoundary section="Track My Issue" apiHint="/api/issues/track"><TrackMyIssue /></HubErrorBoundary>} />
          {/* Legacy URLs — keep working as redirects so existing links never break */}
          <Route path="/action-hub" element={<LegacyHubRedirect to="/issue-tracker" />} />
          <Route path="/action-hub/report" element={<LegacyHubRedirect to="/issue-tracker/report" />} />
          <Route path="/action-hub/track" element={<LegacyHubRedirect to="/issue-tracker/track" />} />
          <Route path="/report-issue" element={<LegacyHubRedirect to="/issue-tracker/report" />} />
          <Route path="/whats-happening" element={<WhatsHappening />} />
          <Route path="/meetings" element={<Meetings />} />
          <Route path="/meetings/:id" element={<MeetingDetail />} />
          <Route path="/city-data" element={<CityData />} />
          <Route path="/tax-tracker" element={<TaxTracker />} />
          <Route path="/infrastructure" element={<InfrastructureTracker />} />
          <Route path="/have-your-say" element={<HowToHaveYourSay />} />
          <Route path="/city-hall-watch" element={<CityHallWatch />} />
          <Route path="/map" element={<HubErrorBoundary section="Public Issue Map" apiHint="/api/map/pins"><PublicMap /></HubErrorBoundary>} />
          <Route path="/issue/:trackingNumber" element={<HubErrorBoundary section="Public Issue Report" apiHint="/api/issues/public"><PublicIssue /></HubErrorBoundary>} />
          <Route path="/savings-tracker" element={<HubErrorBoundary section="Savings Tracker" apiHint="/api/savings"><SavingsTracker /></HubErrorBoundary>} />
          <Route path="/who-do-i-call" element={<HubErrorBoundary section="Who Do I Call" apiHint="/api/directory"><WhoDoICall /></HubErrorBoundary>} />
          <Route path="/reports" element={<HubErrorBoundary section="Accountability Reports" apiHint="/api/reports, /api/issue-stats"><Reports /></HubErrorBoundary>} />
          <Route path="/elections" element={<HubErrorBoundary section="Election Centre" apiHint="/api/election"><Elections /></HubErrorBoundary>} />
          <Route path="/elections/:section" element={<HubErrorBoundary section="Election Centre" apiHint="/api/election"><Elections /></HubErrorBoundary>} />
          <Route path="/election-events" element={<Navigate to="/elections/events" replace />} />
          <Route path="/debates" element={<Navigate to="/elections/events" replace />} />
          <Route path="/whos-running" element={<HubErrorBoundary section="Who's Running" apiHint="/api/candidates"><WhosRunning /></HubErrorBoundary>} />
          <Route path="/find-your-ward" element={<Navigate to={{ pathname: '/whos-running', hash: '#find-your-ward' }} replace />} />
          <Route path="/tracker-network" element={<HubErrorBoundary section="PITA Network" apiHint="/api/network/content"><TrackerNetwork /></HubErrorBoundary>} />
          <Route path="/tracker-network/join" element={<HubErrorBoundary section="Join the Network" apiHint="/api/network/join"><JoinNetwork /></HubErrorBoundary>} />
          <Route path="/pita/request-access" element={<HubErrorBoundary section="Request PITA Access" apiHint="/api/pita/request-access"><PitaRequestAccess /></HubErrorBoundary>} />
          <Route path="/support-pita" element={<HubErrorBoundary section="Support PITA" apiHint="/api/pita/support"><SupportPita /></HubErrorBoundary>} />
          <Route path="/pita-supporters" element={<HubErrorBoundary section="PITA Supporters" apiHint="/api/pita/supporters"><PitaSupporters /></HubErrorBoundary>} />
          <Route path="/across-kawartha-lakes" element={<HubErrorBoundary section="Citywide View" apiHint="/api/network/stats"><CitywideView /></HubErrorBoundary>} />
          <Route path="/tracker/:slug" element={<HubErrorBoundary section="Hosted Tracker" apiHint="/api/network/tracker"><HostedTracker /></HubErrorBoundary>} />
          <Route path="/newsroom" element={<Newsroom />} />
          <Route path="/newsroom/:slug" element={<NewsArticle />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/videos" element={<Videos />} />
          <Route path="/volunteer" element={<Volunteer />} />
          <Route path="/supporters" element={<Supporters />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/alerts" element={<Alerts />} />
        </Route>

        {/* Standalone embeddable widget — intentionally rendered WITHOUT the site layout */}
        <Route path="/embed/tracker/:slug" element={<EmbedTracker />} />

        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="launch" element={<AdminLaunch />} />
          <Route path="launch-content" element={<AdminLaunchContent />} />
          <Route path="designs" element={<AdminDesigns />} />
          <Route path="launch-rehearsal" element={<AdminRehearsal />} />
          <Route path="poster-drops" element={<AdminPosterDrops />} />
          <Route path="materials" element={<AdminMaterials />} />
          <Route path="door-talk" element={<AdminDoorTalk />} />
          <Route path="canvass" element={<AdminCanvassLog />} />
          <Route path="supporters" element={<AdminSupporters />} />
          <Route path="launch-package" element={<AdminLaunchPackage />} />
          <Route path="domain-activation" element={<AdminDomainActivation />} />
          <Route path="launch-runbook" element={<AdminLaunchRunbook />} />
          <Route path="post-launch-pulse" element={<AdminPostLaunchPulse />} />
          <Route path="first-responses" element={<AdminFirstResponses />} />
          <Route path="media-review" element={<AdminMediaReview />} />
          <Route path="privacy" element={<AdminPrivacy />} />
          <Route path="pages" element={<AdminPages />} />
          <Route path="hero" element={<AdminHero />} />
          <Route path="posters" element={<AdminPosters />} />
          <Route path="reviews" element={<AdminReviews />} />
          <Route path="menu" element={<AdminMenu />} />
          <Route path="page-content" element={<AdminPageContent />} />
          <Route path="needs-attention" element={<AdminNeedsAttention />} />
          <Route path="issues" element={<AdminIssues />} />
          <Route path="email-settings" element={<AdminEmailSettings />} />
          <Route path="newsroom" element={<AdminNewsroom />} />
          <Route path="meetings" element={<AdminMeetings />} />
          <Route path="city-data" element={<AdminCityData />} />
          <Route path="alerts" element={<AdminAlerts />} />
          <Route path="newsletter" element={<AdminNewsletter />} />
          <Route path="chat" element={<AdminChat />} />
          <Route path="gallery" element={<AdminGallery />} />
          <Route path="ward8-photos" element={<AdminWard8Photos />} />
          <Route path="image-health" element={<AdminImageHealth />} />
          <Route path="videos" element={<AdminVideoLibrary />} />
          <Route path="stories" element={<AdminStories />} />
          <Route path="story-media" element={<AdminStoryMedia />} />
          <Route path="calendar" element={<AdminCalendar />} />
          <Route path="volunteers" element={<AdminVolunteers />} />
          <Route path="messages" element={<AdminContactInbox />} />
          <Route path="sign-requests" element={<AdminSignRequests />} />
          <Route path="reports" element={<AdminReportsPage />} />
          <Route path="campaign" element={<AdminCampaign />} />
          <Route path="savings" element={<AdminSavings />} />
          <Route path="scheduler" element={<AdminScheduler />} />
          <Route path="network" element={<AdminNetwork />} />
          <Route path="pita-requests" element={<AdminPitaRequests />} />
          <Route path="pita-supporters" element={<AdminPitaSupporters />} />
          <Route path="pita-settings" element={<AdminPitaSettings />} />
          <Route path="directory" element={<AdminDirectory />} />
          <Route path="trackers" element={<AdminTrackers />} />
          <Route path="have-your-say" element={<AdminHaveYourSay />} />
          <Route path="automation" element={<AdminAutomation />} />
          <Route path="election" element={<AdminElection />} />
          <Route path="election-events" element={<AdminElectionEvents />} />
          <Route path="social" element={<AdminSocial />} />
          <Route path="settings" element={<AdminSettings />} />
        </Route>
      </Routes>
      <Toaster position="top-center" richColors />
    </BrowserRouter>
  );
}

export default App;
